import { debounce } from "lodash";
import { useCallback } from "react";
import Skeleton from "react-loading-skeleton";
import { Link, useNavigate, useParams } from "react-router-dom";

import { faMailbox, faMinus, faPencil, faPhone } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import { InfoColumn, InfoColumnLabel, InfoColumnValue, InfoRow } from "components/atoms/InfoRow";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import ModalButton from "components/organisms/ModalButton";
import Pagination from "components/organisms/Pagination";
import useDateTime from "hooks/useDateTime";
import useParamsState from "hooks/useParamsState";
import { AddUserModal, RemoveButton } from "pages/Features/Grupper/AddUserModal";
import { useGetGroupQuery, useGetUsersOnGroupQuery, useRemoveUserFromGroupMutation } from "pages/Features/Grupper/Groups.graphql-gen";

export default function SingleGroupPage() {
	const PER_PAGE = 40;
	const { groupId } = useParams<{ groupId: string }>();
	const { data, loading } = useGetGroupQuery({ variables: { groupId: groupId ?? "" } });
	const [search, setSearch] = useParamsState("search", "");
	const navigate = useNavigate();
	const date = useDateTime();

	const [currentPage, setCurrentPage] = useParamsState("page", 1);
	const {
		data: usersData,
		previousData,
		loading: loadingUsers,
	} = useGetUsersOnGroupQuery({
		variables: {
			groupId: groupId ?? "",
			search: (!!search && search.replaceAll('"', "")) || undefined,
			startIndex: PER_PAGE * (currentPage - 1),
			count: PER_PAGE,
		},
	});

	const [removeUserFromGroup, { loading: removeUserLoading }] = useRemoveUserFromGroupMutation();

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onSearchUpdate = useCallback(
		debounce((newSearch: string) => setSearch(newSearch), 200),
		[]
	);

	const totalPages = (usersData?.group?.users?.total ?? previousData?.group?.users?.total ?? 1) as number;
	return (
		<Flex direction={Direction.column} gap="calc( var(--grid-2) * 2)">
			<MainLayoutPageHeader
				loading={loading}
				title={
					<Flex gap="var(--grid-2)">
						{data?.group?.displayName ?? "Gruppe"}
						<Link to="edit" title={`Rediger ${data?.group?.displayName ?? "Gruppe"}`}>
							<FontAwesomeIcon icon={faPencil} />
						</Link>
					</Flex>
				}
				intro={`${data?.group?.usersCount ?? 0} brukere i gruppen`}
			/>
			{data?.group?.description && <h4 style={{ marginTop: "-20px", marginBottom: "-10px" }}>Beskrivelse: {data?.group?.description}</h4>}
			<Flex direction={Direction.column} gap="calc( var(--grid-2) * 2)">
				<ModalButton label="Legg til brukere i gruppen">
					<h3 style={{ textAlign: "center", marginTop: "20px" }}>Legg til brukere i gruppen</h3>
					<AddUserModal groupId={groupId} />
				</ModalButton>
				<input
					type="text"
					defaultValue={search}
					onChange={(e) => {
						onSearchUpdate(e.target.value);
					}}
					placeholder="Søk etter navn, telefonnummer eller e-post"
				/>
				<Flex direction={Direction.column} gap="var(--grid-2)">
					{loadingUsers &&
						Array.from(Array(PER_PAGE)).map((u, i) => (
							<InfoRow interactive key={i}>
								<InfoColumn width="100%">
									<Skeleton />
								</InfoColumn>
							</InfoRow>
						))}
					{!loadingUsers &&
						usersData?.group?.users?.items?.map((user) => (
							<InfoRow
								interactive
								key={user.userId}
								onClick={() => {
									if (user?.userId) navigate("/users/" + user?.userId);
								}}
							>
								<InfoColumn title="Navn" width="40%">
									<InfoColumnLabel>{user?.name ?? <u>Åpen bruker {user?.nickname ? "(" + user.nickname + ")" : ""}</u>}</InfoColumnLabel>
								</InfoColumn>
								<InfoColumn title="Telefonnummer" width="15%">
									<InfoColumnValue>
										<FontAwesomeIcon icon={faPhone} /> {user?.mobileNumber ?? "―"}
									</InfoColumnValue>
								</InfoColumn>
								<InfoColumn title="E-post" width="10%">
									<InfoColumnValue>
										<FontAwesomeIcon icon={faMailbox} /> {user.emailAddress ?? "--"}
									</InfoColumnValue>
								</InfoColumn>
								<InfoColumn width="20%" />
								<InfoColumn width="15%">
									<RemoveButton
										disabled={removeUserLoading}
										onClick={async (e) => {
											e.preventDefault();
											e.stopPropagation();
											await removeUserFromGroup({
												variables: {
													userId: user.userId ?? "",
													groupId: groupId ?? "",
												},
												refetchQueries: ["GetUsersOnGroup", "GetGroup"],
											});
										}}
									>
										<FontAwesomeIcon icon={faMinus} />
									</RemoveButton>
								</InfoColumn>
							</InfoRow>
						))}
					<Pagination
						total={totalPages}
						perPage={PER_PAGE}
						currentPage={currentPage - 1}
						onChange={(newCurrentPage) => {
							setCurrentPage(newCurrentPage + 1);
							window.scrollTo(0, 0);
						}}
					/>
				</Flex>
			</Flex>
		</Flex>
	);
}
